import React from 'react';
import { Dialog, DialogTitle, useSnackbar } from '@bb-ui/react-library';
import { t } from 'i18next';
import { User } from '../../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { useAssetCheck } from '../../AssetsTransfer/useAssetCheck';
import { useAssetTransfer } from '../../AssetsTransfer/useAssetTransfer';
import { WaitingIfAuthorHasAssetsDialogContent } from '../../AssetsTransfer/dialog/WaitingIfAuthorHasAssetsDialogContent';
import { UserAssetsTransferDialogState } from './UserAssetsTransferDialogState';
import { TransferAssetsDialogContent } from '../../AssetsTransfer/dialog/TransferAssetsDialogContent';
import { ApiFailureDialogContent } from '../../../../ApiFailureDialogContent/ApiFailureDialogContent';

const DIALOG_STATE_TO_TITLE = {
    [UserAssetsTransferDialogState.AssetValidation]:
        'settings.licenseManagement.assetCheck.title',
    [UserAssetsTransferDialogState.TransferAssets]:
        'settings.licenseManagement.assetTransfer.title',
    [UserAssetsTransferDialogState.AssetValidationFail]:
        'settings.licenseManagement.assetCheck.errorTitle',
    [UserAssetsTransferDialogState.TransferAssetsFail]:
        'settings.licenseManagement.assetTransfer.errorTitle',
};

const DIALOG_STATE_TO_ERROR_TEXT = {
    [UserAssetsTransferDialogState.AssetValidationFail]:
        'settings.licenseManagement.assetCheck.errorMessage',
    [UserAssetsTransferDialogState.TransferAssetsFail]:
        'settings.licenseManagement.assetTransfer.errorMessage',
};

export const UserAssetsTransferDialog = (props: {
    userToCheck: User | undefined;
    setUserToCheck: React.Dispatch<React.SetStateAction<User | undefined>>;
    onSuccess: () => void;
}) => {
    const { userToCheck, setUserToCheck, onSuccess } = props;
    const [dialogContentState, setDialogContentState] =
        React.useState<UserAssetsTransferDialogState>(
            UserAssetsTransferDialogState.AssetValidation,
        );
    const { enqueueSnackbar } = useSnackbar();

    const close = React.useCallback(() => {
        setUserToCheck(undefined);
    }, [setUserToCheck]);

    const { checkUserAssets, assetCheckInProgress } = useAssetCheck({
        onAssetsPresent: () => {
            setDialogContentState(UserAssetsTransferDialogState.TransferAssets);
        },
        onAssetsAbsent: () => {
            close();
            onSuccess();
        },
        onAssetCheckError: () => {
            setDialogContentState(
                UserAssetsTransferDialogState.AssetValidationFail,
            );
        },
    });

    const { transferAssets, transferInProgress } = useAssetTransfer({
        onTransferSuccess: () => {
            enqueueSnackbar(
                t('settings.licenseManagement.assetTransfer.snackbarSuccess'),
            );
            close();
            onSuccess();
        },
        onTransferFailure: () => {
            setDialogContentState(
                UserAssetsTransferDialogState.TransferAssetsFail,
            );
        },
    });

    React.useEffect(() => {
        if (userToCheck) {
            checkUserAssets(userToCheck);
            setDialogContentState(
                UserAssetsTransferDialogState.AssetValidation,
            );
        }
    }, [checkUserAssets, userToCheck]);

    return (
        <Dialog
            open={userToCheck !== undefined}
            maxWidth="md"
            disableEscapeKeyDown={assetCheckInProgress || transferInProgress}
            aria-labelledby="user-assets-transfer-dialog-title"
            data-testid="user-assets-transfer-modal"
        >
            <div>
                <DialogTitle
                    id="user-assets-transfer-dialog-title"
                    data-testid="user-assets-transfer-dialog-title"
                    hideCloseButton={true}
                >
                    {dialogContentState
                        ? t(DIALOG_STATE_TO_TITLE[dialogContentState])
                        : ''}
                </DialogTitle>

                {dialogContentState ===
                    UserAssetsTransferDialogState.AssetValidation && (
                    <WaitingIfAuthorHasAssetsDialogContent />
                )}

                {dialogContentState ===
                    UserAssetsTransferDialogState.TransferAssets && (
                    <TransferAssetsDialogContent
                        userToTransferFrom={userToCheck}
                        transferAssets={transferAssets}
                        transferInProgress={transferInProgress}
                        closeDialog={close}
                        infoText={t(
                            'settings.licenseManagement.assetTransfer.infoMessageEdit',
                        )}
                    />
                )}

                {(dialogContentState ===
                    UserAssetsTransferDialogState.AssetValidationFail ||
                    dialogContentState ===
                        UserAssetsTransferDialogState.TransferAssetsFail) && (
                    <ApiFailureDialogContent
                        closeDialog={close}
                        closeTestId="asset-transfer-failure-close"
                        errorText={
                            t(DIALOG_STATE_TO_ERROR_TEXT[dialogContentState]) ||
                            ''
                        }
                    />
                )}
            </div>
        </Dialog>
    );
};
